// Shared timeline merge/sort logic, previously duplicated across OperationsCenterPage
// (TraceTimeline), As2OutboundMessageDetailPage.buildTimeline, and
// As2InboundMessageDetailPage.buildTimeline (the latter two were literal copy-pastes of each
// other). Each call site still maps its own domain records (exchange steps, AS2 milestones,
// etc.) into TimelineEvent objects — that extraction is inherently domain-specific and isn't
// duplicated logic — but the "drop events with no timestamp, sort chronologically" step is,
// and now lives here once. See src/components/Timeline.tsx for the paired render component.

export type TimelineEventTone = 'neutral' | 'success' | 'warning' | 'error';

export interface TimelineEvent {
  id: string;
  label: string;
  detail?: string;
  status?: string;
  at?: string;
  tone?: TimelineEventTone;
  // Marks an event as a routine, expected step (e.g. a step that SUCCEEDED) rather than
  // something an operator specifically needs to see — see groupRoutineRuns in
  // src/components/Timeline.tsx, which collapses runs of 3+ consecutive routine events.
  routine?: boolean;
}

export interface MergedTimelineEvent extends TimelineEvent {
  at: string;
}

export function mergeTimelineEvents(events: TimelineEvent[]): MergedTimelineEvent[] {
  return events
    .filter((event): event is MergedTimelineEvent => Boolean(event.at))
    .sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());
}

const ERROR_STATUSES = new Set(['FAILED', 'DEAD_LETTERED', 'REJECTED', 'DISCARDED', 'MDN_FAILED']);
const WARNING_STATUSES = new Set(['WAITING_RETRY', 'RETRYING', 'RETRY_PENDING', 'PENDING', 'IN_PROGRESS', 'QUEUED', 'SENDING', 'RUNNING', 'REQUESTED', 'QUARANTINED']);
const SUCCESS_STATUSES = new Set(['SUCCEEDED', 'COMPLETED', 'DELIVERED', 'PUBLISHED', 'SENT', 'MDN_RECEIVED', 'PROCESSED', 'REPLAYED']);

// Best-effort status -> visual tone mapping shared across the exchange runtime, AS2 message,
// and customs-message (CUSDEC ML/Etat de Chargement/Tryptique) processingStatus vocabularies —
// distinct enums that use largely overlapping terms.
export function toneForStatus(status?: string): TimelineEventTone {
  if (!status) return 'neutral';
  const normalized = status.toUpperCase();
  if (ERROR_STATUSES.has(normalized)) return 'error';
  if (WARNING_STATUSES.has(normalized)) return 'warning';
  if (SUCCESS_STATUSES.has(normalized)) return 'success';
  return 'neutral';
}

// Builds the unified timeline for one exchange from its steps, execution requests, retry
// attempts, replay requests, and outbox events. Shared by OperationsCenterPage (embedded
// trace panel) and ExchangeDetailPage (full detail view) — both render the exact same
// ExchangeDetails record, and previously showed two different, independently-maintained
// presentations of it (see apps/integration/docs/frontend-ux-audit-2026-08.md §5).
export function buildExchangeTimelineEvents(exchange: {
  status: string;
  documentId?: string;
  updatedAt: string;
  steps: Array<{ id: string; sequenceNumber: number; stepName: string; status: string; traceId?: string; startedAt: string; details?: Record<string, unknown> }>;
  executionRequests: Array<{ id: string; executionType: string; status: string; result?: { summary?: string }; errorMessage?: string; workerId?: string; createdAt: string }>;
  retryAttempts: Array<{ id: string; status: string; reason?: string; requestedBy?: string; requestedAt: string }>;
  replayRequests: Array<{ id: string; status: string; reason?: string; requestedBy?: string; createdAt: string }>;
  outboxEvents: Array<{ id: string; eventType: string; status: string; attemptCount?: number; maxAttempts?: number; createdAt: string }>;
}): MergedTimelineEvent[] {
  const events: TimelineEvent[] = [
    ...exchange.steps.map((step) => ({
      id: `step-${step.id}`,
      label: `${step.sequenceNumber}. ${step.stepName}`,
      status: step.status,
      detail: describeExchangeStep(step),
      at: step.startedAt,
      tone: toneForStatus(step.status),
      routine: step.status === 'SUCCEEDED',
    })),
    ...exchange.executionRequests.map((request) => ({
      id: `execreq-${request.id}`,
      label: `Connector ${request.executionType}`,
      status: request.status,
      detail: request.result?.summary ?? request.errorMessage ?? request.workerId ?? 'execution pending',
      at: request.createdAt,
      tone: toneForStatus(request.status),
      routine: request.status === 'COMPLETED',
    })),
    ...exchange.retryAttempts.map((retry) => ({
      id: `retry-${retry.id}`,
      label: 'Retry requested',
      status: retry.status,
      detail: retry.reason ?? retry.requestedBy ?? 'retry request',
      at: retry.requestedAt,
      tone: 'warning' as const,
      routine: false,
    })),
    ...exchange.replayRequests.map((replay) => ({
      id: `replay-${replay.id}`,
      label: 'Replay requested',
      status: replay.status,
      detail: replay.reason ?? replay.requestedBy ?? 'replay request',
      at: replay.createdAt,
      tone: 'warning' as const,
      routine: false,
    })),
    ...exchange.outboxEvents.map((event) => ({
      id: `outbox-${event.id}`,
      label: `Outbox ${event.eventType}`,
      status: event.status,
      detail: `Attempt ${event.attemptCount ?? 0}/${event.maxAttempts ?? 5}`,
      at: event.createdAt,
      tone: toneForStatus(event.status),
      routine: event.status === 'PUBLISHED',
    })),
    {
      id: 'payload-version',
      label: 'Payload version',
      // No `status` here deliberately: this is a synthetic closing node, and repeating the
      // exchange's own overall status as a second badge would just duplicate the "Status"
      // field every caller already renders elsewhere on the page. `tone` still colors the dot.
      detail: `Document ${exchange.documentId ?? 'n/a'} with ${exchange.executionRequests.length} execution payload snapshots.`,
      at: exchange.updatedAt,
      tone: toneForStatus(exchange.status),
      routine: false,
    },
  ];

  return mergeTimelineEvents(events);
}

// Runtime steps rarely carry a traceId, so the old `traceId ?? 'step trace unavailable'` hid the
// step's recorded outcome (e.g. why GENERATE_DOCUMENT failed validation) behind a placeholder.
export function describeExchangeStep(step: { status: string; traceId?: string; details?: Record<string, unknown> }): string {
  const details = step.details ?? {};
  const errors = Array.isArray(details['errors']) ? details['errors'].filter((item): item is string => typeof item === 'string') : [];
  if (errors.length > 0) {
    const omitted = typeof details['omittedErrorCount'] === 'number' ? details['omittedErrorCount'] : 0;
    return `${errors.join('; ')}${omitted > 0 ? ` (+${omitted} more)` : ''}`;
  }
  const delivery = details['delivery'];
  const deliveryMessage = delivery && typeof delivery === 'object' ? (delivery as Record<string, unknown>)['message'] : undefined;
  if (typeof deliveryMessage === 'string' && deliveryMessage.length > 0) return deliveryMessage;
  if (typeof details['reason'] === 'string' && details['reason'].length > 0) return details['reason'];
  if (details['validationPassed'] === false) return 'Validation failed (no error details were recorded for this step).';
  if (step.traceId) return `trace ${step.traceId}`;
  return step.status === 'FAILED' ? 'Failed (no error details were recorded for this step).' : 'Completed.';
}
